import React, { useMemo } from 'react';
import { Polyline } from 'react-leaflet';
import type { Position, ElevationPoint } from '../../types.js';
import type { ClimbSegment } from '../../utils/climbDetection.js';
import { detectClimbs, DEFAULT_CLIMB_CONFIG, getClimbCategoryColor } from '../../utils/climbDetection.js';

interface ClimbHighlightLayerProps {
  route: Position[];
  elevationData: ElevationPoint[];
  highlightedClimbIndex: number | null;
  climbs?: ClimbSegment[]; // Pre-calculated climbs from full resolution data
}

/**
 * Layer that highlights specific climb segments on the route
 * by creating colored overlays over the matching climb segments
 */
export const ClimbHighlightLayer: React.FC<ClimbHighlightLayerProps> = ({
  route,
  elevationData,
  highlightedClimbIndex,
  climbs: propsClimbs
}) => {
  // Generate highlighted climb segments
  const highlightedSegments = useMemo(() => {
    if (highlightedClimbIndex === null || !elevationData || !route || route.length === 0) {
      return [];
    }

    // Use pre-calculated climbs if available, otherwise detect from elevation data
    const climbs = propsClimbs || detectClimbs(elevationData, DEFAULT_CLIMB_CONFIG);
    
    console.log('🗺️ ClimbHighlightLayer:', {
      highlightedClimbIndex,
      totalClimbs: climbs.length,
      usingPreCalculated: !!propsClimbs,
      climb: climbs[highlightedClimbIndex]
    });
    
    if (!climbs[highlightedClimbIndex]) {
      console.log('❌ No climb found at index', highlightedClimbIndex);
      return [];
    }

    const climb = climbs[highlightedClimbIndex];
    
    // Calculate actual distances for route coordinates using haversine formula
    const routeDistances = [0]; // First point is at distance 0
    const R = 6371000; // Earth's radius in meters
    
    for (let i = 1; i < route.length; i++) {
      const φ1 = route[i - 1].lat * Math.PI / 180;
      const φ2 = route[i].lat * Math.PI / 180;
      const Δφ = (route[i].lat - route[i - 1].lat) * Math.PI / 180;
      const Δλ = (route[i].lng - route[i - 1].lng) * Math.PI / 180;

      const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
               Math.cos(φ1) * Math.cos(φ2) *
               Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const segmentDistance = R * c;
      
      routeDistances.push(routeDistances[i - 1] + segmentDistance);
    }

    // Convert climb distances from km to meters for comparison
    const climbStartDistanceM = climb.startDistance * 1000;
    const climbEndDistanceM = climb.endDistance * 1000;
    
    // Find start coordinate index
    let startCoordIndex = 0;
    for (let i = 0; i < routeDistances.length; i++) {
      if (routeDistances[i] >= climbStartDistanceM) {
        startCoordIndex = Math.max(0, i - 1); // Take previous point to ensure coverage
        break;
      }
    }
    
    // Find end coordinate index
    let endCoordIndex = route.length - 1;
    for (let i = startCoordIndex; i < routeDistances.length; i++) {
      if (routeDistances[i] >= climbEndDistanceM) {
        endCoordIndex = Math.min(route.length - 1, i + 1); // Take next point to ensure coverage
        break;
      }
    }
    
    // Extract the coordinates for this climb segment
    const climbCoordinates = route.slice(startCoordIndex, endCoordIndex + 1);

    console.log('🎯 Climb coordinates:', {
      startCoordIndex,
      endCoordIndex,
      climbCoordsLength: climbCoordinates.length,
      climbStartDistanceKm: climb.startDistance,
      climbEndDistanceKm: climb.endDistance
    });

    if (climbCoordinates.length < 2) {
      console.log('❌ Not enough coordinates for climb');
      return [];
    }

    // Get the climb category color
    const categoryColor = getClimbCategoryColor(climb.category);

    return [{
      coordinates: climbCoordinates,
      color: categoryColor,
      climb: climb
    }];
  }, [route, elevationData, highlightedClimbIndex, propsClimbs]);

  if (highlightedSegments.length === 0) {
    return null;
  }

  return (
    <>
      {highlightedSegments.map((segment, index) => (
        <Polyline
          key={`climb-highlight-${index}`}
          positions={segment.coordinates.map(coord => [coord.lat, coord.lng])}
          pathOptions={{
            color: segment.color,
            weight: 12,
            opacity: 0.9,
            lineCap: 'round',
            lineJoin: 'round',
            dashArray: '8, 4'
          }}
          interactive={false}
        />
      ))}
      
      {/* Add an additional pulsing overlay for better visibility */}
      {highlightedSegments.map((segment, index) => (
        <Polyline
          key={`climb-pulse-${index}`}
          positions={segment.coordinates.map(coord => [coord.lat, coord.lng])}
          pathOptions={{
            color: segment.color,
            weight: 18,
            opacity: 0.4,
            lineCap: 'round',
            lineJoin: 'round',
            dashArray: '4, 2'
          }}
          interactive={false}
        />
      ))}
    </>
  );
};
